/**
 * «Revisar el dibujo»: une la lectura de entidades del visor (una vez por plano) con el cálculo puro de shared.
 *
 *  - Apagado (`habilitado` false) → no lee ni calcula nada.
 *  - Sin visor todavía → diagnóstico PARCIAL (solo lo que sale del inventario: unidad y cotas).
 *  - Con visor → lee las entidades en tramos (abortable al cambiar de plano) y recalcula. Cambiar la unidad o una
 *    decisión de capa SOLO recalcula (`diagnosticarDibujo` es puro y rápido), no vuelve a leer.
 *  - Nunca bloquea «Armar»: si no terminó, simplemente no hay diagnóstico completo todavía.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  diagnosticarDibujo,
  type DecisionDeCapa,
  type DiagnosticoDelDibujo,
  type FactorDeEscala,
  type HuellaDeBloque,
  type ParametrosChequeoPlano,
  type PlanoInventario,
} from '@supernova/shared';
import type { CensoDelPlano } from '../components/PlanoViewerMlightcad.js';
import type { EntidadesLeidas } from './entidadesDelDibujo.js';

/** Pone a cada huella el nombre limpio del inventario (el lector solo conoce el crudo). */
export function conNombresLimpios(huellas: readonly HuellaDeBloque[] | null, inv: PlanoInventario | null): HuellaDeBloque[] | null {
  if (!huellas) return null;
  const limpio = new Map((inv?.bloques ?? []).map((b) => [b.nombre_crudo, b.nombre]));
  return huellas.map((h) => ({ ...h, limpio: limpio.get(h.crudo) ?? h.limpio }));
}

export interface EntradaDelHook {
  habilitado: boolean;
  /** Identifica el plano: al cambiar se descarta la lectura anterior. */
  clave: string;
  censo: CensoDelPlano | null;
  inventario: PlanoInventario | null;
  escala: FactorDeEscala;
  params: ParametrosChequeoPlano;
  /** Deben ser estables (useCallback) y cambiar solo cuando cambia una decisión. */
  decisionCapa: (capa: string) => DecisionDeCapa | null;
  decisionBloque: (nombre: string) => DecisionDeCapa | null;
}

export interface ResultadoDelHook {
  diagnostico: DiagnosticoDelDibujo | null;
  /** true mientras se leen las entidades del visor. */
  leyendo: boolean;
}

export function useDiagnosticoDelDibujo(a: EntradaDelHook): ResultadoDelHook {
  const [lectura, setLectura] = useState<{ clave: string; datos: EntidadesLeidas } | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const { habilitado, clave, censo, params } = a;

  useEffect(() => {
    if (!habilitado || !censo?.entidades) {
      setLeyendo(false);
      return;
    }
    const ctl = new AbortController();
    setLeyendo(true);
    censo
      .entidades({ signal: ctl.signal, maxTrazosPorBloque: params.diag_bloque_huella_max_trazos })
      .then((datos) => {
        if (ctl.signal.aborted) return;
        setLectura({ clave, datos });
        setLeyendo(false);
      })
      .catch(() => {
        if (!ctl.signal.aborted) setLeyendo(false); // sin lectura: queda el diagnóstico parcial
      });
    return () => ctl.abort();
    // params.diag_bloque_huella_max_trazos solo afecta la comparación de bloques; no justifica releer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habilitado, censo, clave]);

  const datos = lectura && lectura.clave === clave ? lectura.datos : null;
  const diagnostico = useMemo(() => {
    if (!habilitado || !a.inventario) return null;
    try {
      return diagnosticarDibujo(
        {
          inventario: a.inventario,
          escala: a.escala,
          entidades: datos?.entidades ?? null,
          huellas: conNombresLimpios(datos?.huellas ?? null, a.inventario),
          decisionCapa: a.decisionCapa,
          decisionBloque: a.decisionBloque,
        },
        params,
      );
    } catch {
      return null; // el diagnóstico nunca tumba la pantalla
    }
  }, [habilitado, a.inventario, a.escala, datos, params, a.decisionCapa, a.decisionBloque]);

  return { diagnostico, leyendo };
}
